/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTabsModule } from "@angular/material/tabs";
import { Router, RouterOutlet } from "@angular/router";
import { EChartsOption, EChartsType } from "echarts";
import { BehaviorSubject, defer, Observable, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DatepickerComponent } from "../../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { LineChart } from "../../../shared/models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { CloudFiltersResponse } from "../../../shared/models/http/dashboard/cloud/cloud-filters-model";
import { KPI } from "../../../shared/models/http/dashboard/kpi.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { CloudAssetsGlobalForm } from "../../../shared/models/ui/cloud-assets-global-form.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { TimePeriodRange } from "../../../shared/models/ui/time-period-range.model";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardCloudService } from "../../../shared/services/dashboard-cloud.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { DashboardStateService } from "../../../shared/services/state/dashboard-state.service";
import { StorageService } from "../../../shared/services/storage.service";
import { ToastService } from "../../../shared/services/toast.service";
import { COLORS, PATHS } from "../../../shared/utils/constants";
import { getCloudAssetsTotalEmissionOverridesForPieChart } from "../../../shared/utils/echarts/chart-utils";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import { getCurrentWeekRange } from "../../../shared/utils/reporting-period";
import {
	convertTabLabelToRoute,
	formatDateForBE,
	mapBase64ToBlob,
	roundValueUpTo,
	saveAs
} from "../../../shared/utils/utils";

type CloudFilters = {
	locations: string[];
	applications: string[];
	cloudProviders: string[];
	startDate: string;
	endDate: string;
};

@Component({
	selector: "globalictcc-cloud-assets",
	standalone: true,
	imports: [
		AsyncPipe,
		RouterOutlet,
		MatTabsModule,
		DatepickerComponent,
		DropdownComponent,
		ButtonComponent,
		ChartComponent,
		ChipsComponent,
		ReactiveFormsModule,
		KpiComponent
	],
	templateUrl: "./cloud-assets.component.html",
	styleUrl: "./cloud-assets.component.scss"
})
export class CloudAssetsComponent implements OnInit, OnDestroy {
	private _pieChartTotalEmissions = signal<number>(0);
	private readonly _document: Document = inject(DOCUMENT);
	public configurationForm = new FormGroup({
		applications: new FormControl<string[]>([], [Validators.required]),
		locations: new FormControl<string[]>([], [Validators.required]),
		cloudProviders: new FormControl<string[]>([], [Validators.required]),
		timePeriod: new FormControl<TimePeriodRange>(getCurrentWeekRange(), {
			validators: [Validators.required]
		})
	});

	private _isFirstRender: boolean = true;
	public readonly grants = GRANTS.cloud;

	//cloud emissions chart
	public cloudPerformanceChartOverrides: EChartsOption = {
		xAxis: {
			axisLabel: {
				formatter: (value: string | number) => this._formatAxisMonthYear(value)
			}
		}
	};

	//filters options
	public applicationOptions: SelectOption[] = [];
	public locationOptions: SelectOption[] = [];
	public cloudProvidersOptions: SelectOption[] = [];
	private _cloudFilters?: CloudFiltersResponse;

	//Pie chart
	public totalEmissionPieChartColors: string[] = [];
	public pieChartOverrides = computed(() =>
		getCloudAssetsTotalEmissionOverridesForPieChart(this._pieChartTotalEmissions())
	);

	private _tenantId: string;
	private _destroy$ = new Subject<void>();

	public chips: ChipOption[] = [
		{ label: "ENVIRONMENTAL IMPACT", value: "ENVIRONMENTAL IMPACT" },
		{ label: "APPLICATIONS", value: "APPLICATIONS" },
		{ label: "LOCATIONS", value: "LOCATIONS" }
	];

	private _predefinedColors = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50
	];

	private readonly _filters$ = new BehaviorSubject<CloudFilters | undefined>(undefined);

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly totalCO2$: Observable<Loadable<KPI>> = lazyLoad(this._filters$, filters =>
		this._dashboardCloudService.getTotalCo2(
			this._tenantId,
			filters.cloudProviders,
			filters.applications,
			filters.locations,
			filters.startDate,
			filters.endDate
		)
	);

	public readonly cloudPerformanceChart$: Observable<Loadable<ChartResponse<LineChart>>> =
		lazyLoad(this._filters$, filters =>
			this._dashboardCloudService.getCloudPerformance(
				this._tenantId,
				filters.cloudProviders,
				filters.applications,
				filters.locations,
				filters.startDate,
				filters.endDate
			)
		);

	public readonly totalEmissionPieChart$: Observable<Loadable<ChartResponse<PieChart>>> =
		lazyLoad(this._filters$, filters =>
			this._dashboardCloudService
				.getCloudEmissionsForPieChart(
					this._tenantId,
					filters.startDate,
					filters.endDate,
					filters.cloudProviders,
					filters.locations,
					filters.applications
				)
				.pipe(tap(pieChart => this._setPieChartTotalsAndColors(pieChart)))
		);

	constructor(
		private _router: Router,
		private _storageService: StorageService,
		private _dashboardCloudService: DashboardCloudService,
		private _ictCarbonInsightsReportService: IctCarbonInsightsReportService,
		private _dashboardStateService: DashboardStateService,
		private _toastService: ToastService,
		public authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this._onFormChange();

		this.chips = this.chips.map(chip => ({
			...chip,
			selected: this._router.url.includes(convertTabLabelToRoute(chip.value.toString()))
		}));

		this._fetchFilters()
			.pipe(takeUntil(this._destroy$))
			.subscribe(() => this._applyFilters());
	}

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	private _formatAxisMonthYear(value: string | number): string {
		if (typeof value === "string" && /^\d{2}-\d{4}$/.test(value)) {
			return value;
		}

		const dateValue = typeof value === "number" ? new Date(value) : new Date(value);

		if (Number.isNaN(dateValue.getTime())) {
			return String(value);
		}

		const month = String(dateValue.getUTCMonth() + 1).padStart(2, "0");
		const year = String(dateValue.getUTCFullYear());

		return `${month}-${year}`;
	}

	/**
	 * Handles the selection of a chip and navigates to the corresponding route.
	 *
	 * @param selectedChip - The selected chip, which can be a string or a number.
	 */
	public handleSelection(selectedChip: string | number) {
		this._router.navigateByUrl(
			`${PATHS.DASHBOARD}/${PATHS.CLOUD}/${convertTabLabelToRoute(selectedChip.toString())}`
		);
	}
	/**
	 * Fetches the cloud provider filters for the current tenant.
	 *
	 * This method calls the `_dashboardCloudService` to retrieve
	 * the cloud filters associated with the tenant identified by `_tenantId`.
	 * If the response is valid, it updates internally:
	 *  - `_cloudFilters` with the received data,
	 *  - `cloudProvidersOptions` with a list of options for the cloud providers,
	 *  - the `configurationForm` by setting the selected cloud providers,
	 *  - and finally updates the form options via `_updateFormOptionsFromCloudProviders`.
	 *
	 * If the response is empty or does not contain cloud providers,
	 * an error is logged to the console.
	 *
	 * @returns {Observable<any>} Observable that emits the service response containing cloud filters.
	 */
	private _fetchFilters() {
		return this._dashboardCloudService.getCloudFilters(this._tenantId).pipe(
			tap(response => {
				if (!response || !response.cloudProviders) {
					console.error("cloud filters error", response);
					return;
				}
				this._cloudFilters = response;
				this.cloudProvidersOptions = response.cloudProviders.map(provider => ({
					label: provider.name,
					value: provider.name
				}));

				const currentCloudProviders = this.configurationForm.controls.cloudProviders.value;
				if (!currentCloudProviders || currentCloudProviders.length === 0) {
					this.configurationForm.controls.cloudProviders.setValue(
						this.cloudProvidersOptions.map(opt => opt.value)
					);
				}

				this._updateFormOptionsFromCloudProviders();
			})
		);
	}

	/**
	 * Publishes the current form values to the widgets and to the sub-pages.
	 */
	private _applyFilters() {
		const formOptions = this._getFormOptions();
		if (!formOptions) return;

		this._dashboardStateService.setCloudAssetsGlobalForm(
			this.configurationForm.value as CloudAssetsGlobalForm
		);
		this._filters$.next(formOptions);
	}

	private _setPieChartTotalsAndColors(pieChart: ChartResponse<PieChart>) {
		const slices = pieChart.chart?.PieChartSlice ?? [];
		this._pieChartTotalEmissions.set(
			roundValueUpTo(slices.reduce((acc, slice) => acc + slice.value, 0))
		);
		this.totalEmissionPieChartColors = slices.map((_, index) =>
			index === slices.length - 1
				? COLORS.GREY_50
				: this._predefinedColors[index % this._predefinedColors.length]
		);
	}

	//FORM METHODS
	private _getFormOptions(): CloudFilters | undefined {
		const { locations, applications, cloudProviders, timePeriod } =
			this.configurationForm.value;

		if (
			!locations ||
			!applications ||
			!cloudProviders ||
			!timePeriod?.start ||
			!timePeriod?.end
		)
			return;

		const { start, end } = timePeriod;

		const startDate = formatDateForBE(start);
		const endDate = formatDateForBE(end);

		if (!startDate || !endDate) return;

		return {
			locations,
			applications,
			cloudProviders,
			startDate,
			endDate
		};
	}

	/**
	 * Handles the apply action by fetching the dashboard data.
	 * This method is triggered when the user applies a certain action
	 * on the dashboard.
	 *
	 * @private
	 */
	public onApply() {
		this._applyFilters();
	}

	/**
	 * Handles changes to the configuration form.
	 *
	 * This method subscribes to the `valueChanges` observable of the `configurationForm` and performs
	 * the following actions when the form changes:
	 * - Updates the global form state in the `_dashboardStateService`.
	 * - Filters and maps the applications to update the `layerOptions` based on the selected applications.
	 * - On the first render, sets the `layers` control value to include all available layer options.
	 *
	 * The subscription is automatically unsubscribed when the component is destroyed using the `_destroy$` observable.
	 *
	 * @private
	 */
	private _onFormChange() {
		this.configurationForm.controls.cloudProviders.valueChanges
			.pipe(
				tap(selectedCloudProviders => {
					const previousLocations = this.configurationForm.controls.locations.value;
					const previousApplications = this.configurationForm.controls.applications.value;

					const selectedProviders = this._cloudFilters!.cloudProviders.filter(provider =>
						selectedCloudProviders?.includes(provider.name)
					);

					const allLocations = selectedProviders
						.flatMap(provider => provider.locations)
						.filter(
							(location, index, self) =>
								self.findIndex(l => l.name === location.name) === index
						);

					this.locationOptions = allLocations.map(location => ({
						label: location.name,
						value: location.name
					}));

					const newLocations = (previousLocations || []).filter(location =>
						this.locationOptions.some(option => option.value === location)
					);
					this.configurationForm.controls.locations.setValue(newLocations);

					const filteredLocations = allLocations.filter(location =>
						newLocations.includes(location.name)
					);

					const effectiveLocations =
						filteredLocations.length > 0 ? filteredLocations : allLocations;

					const allApplications = effectiveLocations
						.flatMap(location => location.applications)
						.filter((app, index, self) => self.indexOf(app) === index);

					this.applicationOptions = allApplications.map(
						(app): SelectOption => ({
							label: app,
							value: app
						})
					);

					const newApplications = (previousApplications || []).filter(app =>
						this.applicationOptions.some((opt: SelectOption) => opt.value === app)
					);
					this.configurationForm.controls.applications.setValue(newApplications);

					if (this._isFirstRender) {
						this._isFirstRender = false;
						this.configurationForm.controls.locations.setValue(
							this.locationOptions.map(option => option.value)
						);
						this.configurationForm.controls.applications.setValue(
							this.applicationOptions.map(option => option.value)
						);
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 *  Updates  the form's  location  and application  options  based on  the  currently selected  cloud  providers.
	 *
	 *  This  method performs  the  following steps:
	 * -  Filters  the available  cloud  providers from  `_cloudFilters`  using the  selected  values in  the  form.
	 *  - Aggregates  all  unique locations  from  the selected  providers  and updates  `locationOptions`.
	 *  -  Validates the  current  location selections  against  the new  options  and updates  the  form control.
	 * -  Determines  the valid  set  of applications  based  on the  selected  or fallback  locations.
	 *  -  Aggregates all  unique  applications and  updates  `applicationOptions`.
	 *  - Validates  the  current application  selections  against the  new  options and  updates  the form  control.
	 *
	 * This  ensures  that the  form  remains consistent  and  only reflects  valid  combinations of  cloud  providers,
	 *  locations, and  applications,  preventing stale  or  invalid selections.
	 *
	 *  @private
	 */
	private _updateFormOptionsFromCloudProviders() {
		const selectedCloudProviders = this.configurationForm.controls.cloudProviders.value;
		const selectedProviders = this._cloudFilters!.cloudProviders.filter(provider =>
			selectedCloudProviders?.includes(provider.name)
		);

		const allLocations = selectedProviders
			.flatMap(provider => provider.locations)
			.filter(
				(location, index, self) => self.findIndex(l => l.name === location.name) === index
			);

		this.locationOptions = allLocations.map(location => ({
			label: location.name,
			value: location.name
		}));

		const currentLocations = this.configurationForm.controls.locations.value || [];
		const validLocations = currentLocations.filter(location =>
			this.locationOptions.some(option => option.value === location)
		);

		this.configurationForm.controls.locations.setValue(
			validLocations.length > 0
				? validLocations
				: this.locationOptions.map(option => option.value)
		);

		const allApplications = (
			validLocations.length > 0
				? allLocations.filter(location => validLocations.includes(location.name))
				: allLocations
		)
			.flatMap(location => location.applications)
			.filter((app, index, self) => self.indexOf(app) === index);

		this.applicationOptions = allApplications.map(app => ({
			label: app,
			value: app
		}));

		const currentApplications = this.configurationForm.controls.applications.value || [];
		const validApplications = currentApplications.filter(app =>
			this.applicationOptions.some(option => option.value === app)
		);

		this.configurationForm.controls.applications.setValue(
			validApplications.length > 0
				? validApplications
				: this.applicationOptions.map(option => option.value)
		);
	}

	/**
	 * Initiates the export of a report in CSV format based on the current form options.
	 * If the form options are not available, the export process is aborted.
	 *
	 * The export process involves the following steps:
	 * 1. Retrieve the form options including start date, end date, applications, and layers.
	 * 2. Call the exportResponseInCsv method of the dashboard general service with the retrieved options and tenant ID.
	 * 3. Subscribe to the export response to handle the CSV export process.
	 */
	public onDownloadAsCSV() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		defer(() => {
			this._toastService.success(
				"Download started",
				"Your CSV download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._dashboardCloudService.exportResponseInCsv(
				formOptions.startDate,
				formOptions.endDate,
				formOptions.cloudProviders,
				formOptions.applications,
				formOptions.locations,
				this._tenantId
			);
		})
			.pipe(
				map(csvBlob => URL.createObjectURL(csvBlob)),
				tap({
					next: (url: string) => {
						saveAs(
							this._document,
							url,
							`${environment.dashboardExportFileName}_${new Date().toLocaleDateString()}.csv`
						);
						URL.revokeObjectURL(url); // Free the memory
					}
				})
			)
			.subscribe();
	}

	public onExportInsightsReport(): void {
		const formOptions = this._getFormOptions();
		if (!formOptions) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.CLOUD, {
				from: formOptions.startDate,
				to: formOptions.endDate
			})
			.subscribe();
	}

	/**
	 * Initiates the export of a report in PDF format based on the current form options.
	 *
	 * This method retrieves the form options, constructs the report parameters,
	 * and calls the dashboard general service to export the report. The export
	 * process is terminated if the component is destroyed.
	 *
	 * @returns This method does not return a value.
	 */
	public onExportReport() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		const reportParams = {
			applications: formOptions.applications,
			from: formOptions.startDate,
			to: formOptions.endDate
		};

		this._dashboardCloudService
			.exportReportInPDF(reportParams, this._tenantId)
			.pipe(
				map(pdfBase64 => {
					const blob = mapBase64ToBlob(pdfBase64, "application/pdf");
					return URL.createObjectURL(blob);
				}),
				tap({
					next: (url: string) =>
						saveAs(
							this._document,
							url,
							`${environment.dashboardExportFileName}_${new Date().toLocaleDateString()}.pdf`
						)
				})
			)
			.subscribe();
	}
}
